import { supabase } from "@/integrations/supabase/client";

export interface Language {
  id: string;
  code: string;
  name: string;
  native_name: string;
  flag_emoji: string;
  is_default: boolean;
  is_active: boolean;
  words_count: number;
  translated_count: number;
  created_at: string;
  updated_at: string;
}

// Shared cache for getActive to prevent duplicate requests
let _activeLanguagesCache: { promise: Promise<Language[]> | null; data: Language[] | null; timestamp: number } = {
  promise: null, data: null, timestamp: 0
};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const languagesService = {
  async getAll(): Promise<Language[]> {
    const { data, error } = await supabase
      .from('languages')
      .select('*')
      .order('is_default', { ascending: false })
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getActive(): Promise<Language[]> {
    const now = Date.now();
    // Return cached data if fresh
    if (_activeLanguagesCache.data && (now - _activeLanguagesCache.timestamp) < CACHE_TTL) {
      return _activeLanguagesCache.data;
    }
    // Deduplicate in-flight requests
    if (_activeLanguagesCache.promise) {
      return _activeLanguagesCache.promise;
    }
    _activeLanguagesCache.promise = (async () => {
      const { data, error } = await supabase
        .from('languages')
        .select('*')
        .eq('is_active', true)
        .order('is_default', { ascending: false })
        .order('name');
      if (error) throw error;
      const result = data || [];
      _activeLanguagesCache.data = result;
      _activeLanguagesCache.timestamp = Date.now();
      _activeLanguagesCache.promise = null;
      return result;
    })();
    return _activeLanguagesCache.promise;
  },

  async updateStats(): Promise<void> {
    const { error } = await supabase.rpc('update_language_stats');
    if (error) throw error;
  },

  async toggleActive(id: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('languages')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) throw error;
  }
};
